// TanStack Query hooks for coach plan adjustments — re-planning a few days
// when availability or equipment changes, without a full coach rebuild.
//
// Applying a proposal reuses `useBulkApplyMutation` (coach-actions.ts): an
// adjustment's suggestions are ordinary activity + details rows on their own
// coach call.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from './api';
import {
  CoachAdjustDiscardResponseSchema,
  CoachAdjustOpenResponseSchema,
  CoachAdjustResponseSchema,
  type CoachAdjustDiscardResponse,
  type CoachAdjustOpenResponse,
  type CoachAdjustResponse,
} from './types';

export const OPEN_ADJUSTMENT_KEY = ['coach-adjust-open'] as const;

export type AdjustChange = 'no_speediance' | 'short_on_time' | 'unavailable';

export type AdjustRequest =
  | {
      start_date: string;
      end_date: string;
      change: AdjustChange;
      /** 10–240; required by the server for 'short_on_time'. */
      minutes?: number;
      note?: string;
    }
  | { constraint_window_id: number };

/** Ask the coach to re-plan around a change. Takes ~20–60 s (a model call). */
export function useRequestAdjustment() {
  const qc = useQueryClient();
  return useMutation<CoachAdjustResponse, Error, AdjustRequest>({
    mutationFn: (body) =>
      apiClient.post<CoachAdjustResponse>('/api/coach/adjust', body, {
        schema: CoachAdjustResponseSchema,
        // A network failure must not land in the write-retry queue: replaying
        // it later would fire a second model call the operator never asked for.
        stashOnNetworkError: false,
      }),
    // The server writes the constraint window before the model runs, so the
    // Settings list changes even when the call itself fails.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['constraint_windows'] });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OPEN_ADJUSTMENT_KEY });
      qc.invalidateQueries({ queryKey: ['coach-latest'] });
    },
  });
}

/** The latest adjustment still awaiting a decision, or null. */
export function useOpenAdjustment() {
  return useQuery<CoachAdjustOpenResponse>({
    queryKey: OPEN_ADJUSTMENT_KEY,
    queryFn: () =>
      apiClient.get<CoachAdjustOpenResponse>('/api/coach/adjust/open', {
        schema: CoachAdjustOpenResponseSchema,
      }),
  });
}

/** Decline a proposal. Its constraint window stays (see Settings). */
export function useDiscardAdjustment() {
  const qc = useQueryClient();
  return useMutation<CoachAdjustDiscardResponse, Error, { coachCallId: number }>({
    mutationFn: ({ coachCallId }) =>
      apiClient.post<CoachAdjustDiscardResponse>(
        `/api/coach/adjust/${coachCallId}/discard`,
        undefined,
        { schema: CoachAdjustDiscardResponseSchema },
      ),
    // Awaited so DISCARD stays pending until the proposal's own data shows it
    // closed — otherwise the buttons re-enable on a proposal that is gone.
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: OPEN_ADJUSTMENT_KEY });
      await qc.invalidateQueries({ queryKey: ['coach-latest'] });
    },
  });
}
